/**
 * Checkpoint PATCH fields after IQ `video_to_components/` returns (inline MDP-452 flow).
 *
 * @param {{ videoType?: string, testingType?: string } | null} microlearningSelection
 * @returns {"quiz_only"|"inspection_only"|"both"}
 */
export function getVideoToComponentsTestingType(microlearningSelection) {
  const t = microlearningSelection?.testingType;
  if (t === "quiz_only" || t === "inspection_only") return t;
  return "both";
}

export function defaultPassToolbarTabForTestingType(testingType) {
  if (testingType === "quiz_only") return 1;
  if (testingType === "inspection_only") return 2;
  return 0;
}

export function suggestedInstructionsHasQuiz(suggestedInstructions) {
  if (!suggestedInstructions || typeof suggestedInstructions !== "object") return false;
  const quiz = suggestedInstructions.quiz;
  if (Array.isArray(quiz)) return quiz.length > 0;
  if (quiz && typeof quiz === "object") {
    if (Array.isArray(quiz.questions) && quiz.questions.length > 0) return true;
    if (Array.isArray(quiz.items) && quiz.items.length > 0) return true;
  }
  if (
    Array.isArray(suggestedInstructions.questions) &&
    suggestedInstructions.questions.length > 0
  ) {
    return true;
  }
  return false;
}

export function suggestedInstructionsHasInspectionProtocol(suggestedInstructions) {
  if (!suggestedInstructions || typeof suggestedInstructions !== "object") return false;
  const nestedSteps = suggestedInstructions.inspection_instructions?.instructions?.steps;
  if (Array.isArray(nestedSteps) && nestedSteps.length > 0) return true;
  const directInspectionSteps = suggestedInstructions.inspection_instructions?.steps;
  if (Array.isArray(directInspectionSteps) && directInspectionSteps.length > 0) return true;
  const videoInstructions = suggestedInstructions.video_instructions;
  if (videoInstructions && typeof videoInstructions === "object") {
    const viSteps = videoInstructions.instructions?.steps ?? videoInstructions.steps;
    if (Array.isArray(viSteps) && viSteps.length > 0) return true;
  }
  if (Array.isArray(suggestedInstructions.steps) && suggestedInstructions.steps.length > 0) {
    return true;
  }
  return false;
}

function serializeIfNeeded(value) {
  if (value == null) return null;
  return typeof value === "string" ? value : JSON.stringify(value);
}

function hasPassTriple(
  suggestedInstructions,
  tableOfContents,
  quizPayload,
  videoInstructionsPayload
) {
  return (
    tableOfContents != null &&
    quizPayload != null &&
    videoInstructionsPayload != null &&
    ((Array.isArray(tableOfContents?.chapters) && tableOfContents.chapters.length > 0) ||
      (Array.isArray(suggestedInstructions?.chapters) && suggestedInstructions.chapters.length > 0))
  );
}

/**
 * @param {Object} params
 * @param {Object} params.suggestedInstructions - IQ suggestions payload
 * @param {"pass"|"fail"} params.mode
 * @param {{ videoType?: string, testingType?: string } | null} [params.microlearningSelection]
 * @returns {Object} Fields for `changeCheckpointV2`
 */
export function buildVideoToComponentsCheckpointPatch({
  suggestedInstructions,
  mode,
  microlearningSelection = null,
}) {
  if (mode === "fail") {
    const reasoning =
      suggestedInstructions?.error ??
      suggestedInstructions?.validation?.reasoning ??
      suggestedInstructions?.inspection_instructions?.validation?.reasoning ??
      "Video validation failed.";
    const videoUrl = suggestedInstructions?.video_url ?? null;
    return {
      proposed_video_microlearning_is_mismatch: true,
      proposed_instructions: JSON.stringify({
        error: reasoning,
        video_url: videoUrl,
        validation: {
          status: "FAIL",
          reasoning,
        },
      }),
      proposed_quiz: null,
      proposed_inspection_protocol: null,
      proposed_quiz_is_autopass: false,
    };
  }

  const testingType = getVideoToComponentsTestingType(microlearningSelection);
  const tableOfContents =
    suggestedInstructions?.table_of_contents ??
    (Array.isArray(suggestedInstructions?.chapters)
      ? { chapters: suggestedInstructions.chapters }
      : null);
  const quizPayload = suggestedInstructions?.quiz ?? null;
  const videoInstructionsPayload =
    suggestedInstructions?.inspection_instructions ??
    suggestedInstructions?.video_instructions ??
    null;

  const base = {
    proposed_video_microlearning_is_mismatch: false,
    microlearning_instructions: suggestedInstructions,
  };

  if (testingType === "quiz_only") {
    return {
      ...base,
      proposed_instructions: null,
      proposed_quiz: serializeIfNeeded(quizPayload),
      proposed_inspection_protocol: null,
      proposed_quiz_is_autopass: true,
    };
  }

  if (testingType === "inspection_only") {
    return {
      ...base,
      proposed_instructions: null,
      proposed_quiz: null,
      proposed_inspection_protocol: serializeIfNeeded(videoInstructionsPayload),
      proposed_quiz_is_autopass: false,
    };
  }

  const triple = hasPassTriple(
    suggestedInstructions,
    tableOfContents,
    quizPayload,
    videoInstructionsPayload
  );

  return {
    ...base,
    proposed_instructions: triple ? serializeIfNeeded(tableOfContents) : null,
    proposed_quiz: triple ? serializeIfNeeded(quizPayload) : null,
    proposed_inspection_protocol: triple ? serializeIfNeeded(videoInstructionsPayload) : null,
  };
}

function parseJsonField(value) {
  if (value == null || value === "") return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
}

/** Rich PASS draft: TOC + quiz + inspection protocol on checkpoint microlearning_instructions. */
export function microlearningInstructionsLooksLikePassTriple(ml) {
  if (!ml || typeof ml !== "object") return false;
  const statusRaw = ml.validation?.status ?? ml.inspection_instructions?.validation?.status ?? null;
  const statusUpper = typeof statusRaw === "string" ? statusRaw.toUpperCase() : "";
  if (statusUpper !== "PASS") return false;
  const hasQuiz = ml.quiz != null && typeof ml.quiz === "object";
  const hasToc =
    (Array.isArray(ml.table_of_contents?.chapters) && ml.table_of_contents.chapters.length > 0) ||
    (Array.isArray(ml.chapters) && ml.chapters.length > 0);
  const hasProtocol =
    ml.inspection_instructions != null &&
    typeof ml.inspection_instructions === "object" &&
    Array.isArray(ml.inspection_instructions.steps) &&
    ml.inspection_instructions.steps.length > 0;
  return hasQuiz && hasToc && hasProtocol;
}

/**
 * Checkpoint already has generated microlearning kit (quiz / inspection / TOC tabs).
 *
 * @param {Object|null|undefined} checkpoint
 * @returns {boolean}
 */
export function checkpointHasGeneratedVideoMicrolearningKit(checkpoint) {
  if (!checkpoint || typeof checkpoint !== "object") return false;

  const proposedQuiz = parseJsonField(checkpoint.proposed_quiz);
  if (suggestedInstructionsHasQuiz({ quiz: proposedQuiz })) return true;

  const proposedProtocol = parseJsonField(checkpoint.proposed_inspection_protocol);
  if (suggestedInstructionsHasInspectionProtocol(proposedProtocol)) return true;

  const proposedInstructions = parseJsonField(checkpoint.proposed_instructions);
  if (proposedInstructions && typeof proposedInstructions === "object") {
    const failStatus =
      typeof proposedInstructions.validation?.status === "string"
        ? proposedInstructions.validation.status.toUpperCase()
        : "";
    const isFailPayload = !!(proposedInstructions.error || failStatus === "FAIL");
    if (!isFailPayload) {
      const toc = proposedInstructions.table_of_contents?.chapters ?? proposedInstructions.chapters;
      if (Array.isArray(toc) && toc.length > 0) return true;
      if (Array.isArray(proposedInstructions.steps) && proposedInstructions.steps.length > 0) {
        return true;
      }
    }
  }

  const mlRaw = checkpoint.microlearning_instructions;
  const ml = parseJsonField(mlRaw) ?? (typeof mlRaw === "object" ? mlRaw : null);
  if (ml && typeof ml === "object") {
    const statusUpper =
      typeof ml.validation?.status === "string" ? ml.validation.status.toUpperCase() : "";
    if (statusUpper !== "FAIL") {
      if (microlearningInstructionsLooksLikePassTriple(ml)) return true;
      if (suggestedInstructionsHasQuiz(ml) || suggestedInstructionsHasInspectionProtocol(ml)) {
        return true;
      }
      const toc = ml.table_of_contents?.chapters ?? ml.chapters;
      if (Array.isArray(toc) && toc.length > 0) return true;
    }
  }

  return false;
}

export function isInlineVideoToComponentsPassSuccess(
  suggestedInstructions,
  microlearningSelection
) {
  if (!suggestedInstructions || suggestedInstructions.error) return false;
  const status =
    suggestedInstructions.validation?.status ??
    suggestedInstructions.inspection_instructions?.validation?.status ??
    null;
  const statusUpper = typeof status === "string" ? status.toUpperCase() : "";
  if (statusUpper === "PASS") return true;

  const testingType = getVideoToComponentsTestingType(microlearningSelection);
  if (testingType === "quiz_only") {
    return suggestedInstructionsHasQuiz(suggestedInstructions);
  }
  if (testingType === "inspection_only") {
    return suggestedInstructionsHasInspectionProtocol(suggestedInstructions);
  }
  return false;
}
